import React, {StrictMode} from 'react';
import {BrowserRouter} from 'react-router-dom';

import {BehaviorsProvider} from 'common/contexts/react/behaviors-context';
import {BookmarkProvider} from 'common/contexts/react/bookmark-context';
import {CurrentProjectProvider} from 'common/contexts/react/current-project-context';
import {CurrentUserProvider} from 'common/contexts/react/current-user-context';
import {CustomEventsProvider} from 'common/contexts/react/custom-events-context';
import {DashboardSelectorControllerProvider} from 'common/contexts/react/dashboard-selector-controller-context';
import {DataGroupProvider} from 'common/contexts/react/data-group-context';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {DragAndDropManagerProvider} from 'common/contexts/react/drag-and-drop-manager-context';
import {EventPropertyDefinitionsProvider} from 'common/contexts/react/event-property-definition-context';
import {EventDefinitionsProvider as LegacyEventDefinitionsProvider} from 'common/contexts/react/legacy-event-definition-context';
import {GlobalSearchControllerProvider} from 'common/contexts/react/global-search-controller-context';
import {HistoryManagerProvider} from 'common/contexts/react/history-manager-context';
import {
  LocalStorageManagerProvider,
  SessionStorageManagerProvider,
} from 'common/contexts/react/storage-manager-context';
import {LookupTablesProvider} from 'common/contexts/react/lookup-tables-context';
import {MetricsResourceProvider} from 'common/contexts/react/metrics-resource-context';
import {OpenInNewTabProvider} from 'common/contexts/react/open-in-new-tab-context';
import {PeoplePropertyDefinitionsProvider} from 'common/contexts/react/people-property-definition-context';
import {ProjectsProvider} from 'common/contexts/react/projects-context';
import {ProjectTopCreatorsProvider} from 'common/contexts/react/project-top-creators-context';
import {Providers} from 'common/util/react';
import {QueryContextManagerProvider} from 'common/contexts/react/query-context-manager';
import {ReportEditorActionsProvider} from 'common/contexts/react/report-editor-actions-context';
import {ReportLayoutManagerProvider} from 'common/contexts/react/report-layout-manager-context';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {SegmentFilterControllerProvider} from 'common/contexts/react/segment-filter-controller-context';
import {lookupTablesStore} from 'common/report/stores/lookup-tables';
import {eventPropertyDefinitionsStore, peoplePropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {customEventsStore} from 'common/report/stores/custom-events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {BrowserContextRoot} from 'common/widgets/browser-context-root';
import {EventDefinitionsProvider} from 'common/contexts/react/event-definitions-context';

import {Routes} from './routes';

export const App = () => {
  const {
    behaviors,
    bookmarkStore,
    currentProject,
    currentUser,
    dashboardSelectorController,
    dragAndDropManager,
    eventDefinitions,
    globalSearchController,
    historyManager,
    localStorageManager,
    metrics,
    projects,
    projectTopCreators,
    queryContextManager,
    reportEditorActions,
    reportLayoutManager,
    sessionStorageManager,
  } = document.querySelector<BrowserContextRoot>(`mp-browser-context-root`).getConfig(`defaultContexts`);

  const providers = [
    // report, boards and qb
    <DashboardSelectorControllerProvider value={dashboardSelectorController} />,
    <DragAndDropManagerProvider value={dragAndDropManager} />,
    <HistoryManagerProvider value={historyManager} />,

    // window.open has to have `window` as context, force binding it here
    <OpenInNewTabProvider value={window.open.bind(window)} />,
    <QueryContextManagerProvider value={queryContextManager} />,
    <ReportEditorActionsProvider value={reportEditorActions} />,
    <ReportLayoutManagerProvider value={reportLayoutManager} />,
    <SegmentFilterControllerProvider value={new SegmentFilterController()} />,

    // localStorage & sessionStorage
    <LocalStorageManagerProvider value={localStorageManager} />,
    <SessionStorageManagerProvider value={sessionStorageManager} />,

    // common model resources
    <BehaviorsProvider value={behaviors} />,
    <BookmarkProvider value={bookmarkStore} />,
    <CurrentProjectProvider value={currentProject} />,
    <CurrentUserProvider value={currentUser} />,
    <GlobalSearchControllerProvider value={globalSearchController} />,
    <MetricsResourceProvider value={metrics} />,
    <ProjectsProvider value={projects} />,
    <ProjectTopCreatorsProvider value={projectTopCreators} />,
    <EventDefinitionsProvider value={eventDefinitions} />,

    // legacy stores we use
    <CustomEventsProvider value={customEventsStore} />,
    <DataGroupProvider value={dataGroupStore} />,
    <LegacyEventDefinitionsProvider value={eventDefinitionsStore} />,
    <EventPropertyDefinitionsProvider value={eventPropertyDefinitionsStore} />,
    <LookupTablesProvider value={lookupTablesStore} />,
    <PeoplePropertyDefinitionsProvider value={peoplePropertyDefinitionsStore} />,
  ];

  return (
    <StrictMode>
      <Providers providers={providers}>
        <BrowserRouter>
          <Routes />
        </BrowserRouter>
      </Providers>
    </StrictMode>
  );
};
